import type {
  DataFetchParams,
  DataFetchResponse,
  FilterOptionsParams,
  DynamicFilterOptionsPage,
} from '@/components/DataTable';
import {
  listAiInstructionsDataTable,
  getAiInstructionFilterOptions,
} from '@kirin/api-client/api/ai-instructions';
import type { AiInstructionDataTableItem } from '@kirin/api-client/contracts/ai-instructions';
import type {
  AiInstructionFilterOptionField,
  AiInstructionDataTableSortField,
} from '@kirin/api-client/contracts/ai-instructions';
import { AI_INSTRUCTION_FILTER_OPTION_FIELDS } from '@kirin/api-client/contracts/ai-instructions';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/hooks/api/queryKeys';

/** Fetch AI instructions with DataTable pagination, filters, and sorting. */
export async function fetchAiInstructions(
  organizationId: string,
  params: DataFetchParams,
  signal?: AbortSignal
): Promise<DataFetchResponse<AiInstructionDataTableItem>> {
  return listAiInstructionsDataTable(
    apiClient,
    organizationId,
    {
      page: params.pagination.pageIndex + 1,
      limit: params.pagination.pageSize,
      filters: params.filters,
      sortBy: params.sort?.field as AiInstructionDataTableSortField | undefined,
      sortOrder: params.sort?.order,
      search: params.search,
      knownTotal: params.knownTotal,
    },
    { signal }
  );
}

/**
 * Fetch distinct filter option values for a server-backed AI instruction field.
 * Used for the Name and File columns.
 */
export async function fetchAiInstructionFilterOptions(
  organizationId: string,
  field: AiInstructionFilterOptionField,
  params: FilterOptionsParams
): Promise<DynamicFilterOptionsPage> {
  const response = await getAiInstructionFilterOptions(
    apiClient,
    organizationId,
    {
      field,
      search: params.search,
      limit: params.limit ?? 10,
      offset: params.offset ?? 0,
    }
  );
  return {
    options: response.options,
    total: response.pagination.total,
    nextOffset: response.pagination.nextOffset ?? null,
    hasMore: response.pagination.hasMore,
  };
}

/** Query key for server-backed filter options (name, file, projects, users). */
export function getAiInstructionFilterOptionsQueryKey(
  organizationId: string,
  field: AiInstructionFilterOptionField,
  params: { search?: string; limit?: number; offset?: number }
): readonly unknown[] {
  return queryKeys.aiInstructions.filterOptions(organizationId, field, params);
}

/**
 * Fetch users who have AI instructions for the Users column filter.
 * Server-backed — returns only users with at least one AI instruction.
 */
export async function fetchAiInstructionUserFilterOptions(
  organizationId: string,
  params: FilterOptionsParams
): Promise<DynamicFilterOptionsPage> {
  const response = await getAiInstructionFilterOptions(
    apiClient,
    organizationId,
    {
      field: AI_INSTRUCTION_FILTER_OPTION_FIELDS.USERS,
      search: params.search,
      limit: params.limit ?? 10,
      offset: params.offset ?? 0,
    }
  );

  // Use optionsWithMetadata if available (for users field), otherwise fall back to options
  const options = response.optionsWithMetadata
    ? response.optionsWithMetadata.map(o => ({
        value: o.value,
        label: o.label,
        metadata: { name: o.name, email: o.email },
      }))
    : response.options.map(o => ({ value: o, label: o }));

  return {
    options,
    total: response.pagination.total,
    nextOffset: response.pagination.nextOffset ?? null,
    hasMore: response.pagination.hasMore,
  };
}

/** Query key for user filter options (server-backed). */
export function getAiInstructionUserFilterOptionsQueryKey(
  organizationId: string,
  params: FilterOptionsParams
): readonly unknown[] {
  return queryKeys.aiInstructions.filterOptions(
    organizationId,
    AI_INSTRUCTION_FILTER_OPTION_FIELDS.USERS,
    params
  );
}

/**
 * Fetch Project entities (KIR-3356) for the Projects column filter.
 * Server-backed — returns only projects that own at least one AI instruction.
 * Option value = project slug (stable filter key); label = project name.
 */
export async function fetchAiInstructionProjectFilterOptions(
  organizationId: string,
  params: FilterOptionsParams
): Promise<DynamicFilterOptionsPage> {
  const response = await getAiInstructionFilterOptions(
    apiClient,
    organizationId,
    {
      field: AI_INSTRUCTION_FILTER_OPTION_FIELDS.PROJECTS,
      search: params.search,
      limit: params.limit ?? 10,
      offset: params.offset ?? 0,
    }
  );

  const options = response.optionsWithMetadata
    ? response.optionsWithMetadata.map(o => ({
        value: o.value,
        label: o.label,
      }))
    : response.options.map(o => ({ value: o, label: o }));

  return {
    options,
    total: response.pagination.total,
    nextOffset: response.pagination.nextOffset ?? null,
    hasMore: response.pagination.hasMore,
  };
}

/** Query key for project filter options (server-backed). */
export function getAiInstructionProjectFilterOptionsQueryKey(
  organizationId: string,
  params: FilterOptionsParams
): readonly unknown[] {
  return queryKeys.aiInstructions.filterOptions(
    organizationId,
    AI_INSTRUCTION_FILTER_OPTION_FIELDS.PROJECTS,
    params
  );
}
